/**
 * The Studio's "Add columns" groups, read from COLUMN_DEFS.
 *
 * A hard-coded list of group names hid every column whose group was not on it,
 * so a column added to COLUMN_DEFS under a new group could never be added to a
 * report. The groups come from the definitions; the established ones keep the
 * order the Studio has always shown them in, and any other follows.
 */
import { COLUMN_DEFS } from './columns'
import type { ColumnDef } from './columnTypes'
import type { ColumnGroup, ColumnId } from './types'

const ESTABLISHED_ORDER = ['Identity', 'Structure', 'Credit', 'Workflow', 'Governance']

export function columnGroups(): ColumnGroup[] {
  const present = new Set<string>(COLUMN_DEFS.map((column) => column.group))
  const known = ESTABLISHED_ORDER.filter((group) => present.has(group))
  const rest = [...present].filter((group) => !ESTABLISHED_ORDER.includes(group))
  return [...known, ...rest] as ColumnGroup[]
}

/** The words of a label, a group or an id ("debtYieldStabilized" is debt, yield,
 *  stabilized), plus the whole id, lower-cased. */
function searchTokens(column: ColumnDef): { label: string[]; all: string[] } {
  const words = (text: string) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
  const label = words(column.label)
  const idWords = words(column.id.replace(/([a-z0-9])([A-Z])/g, '$1 $2'))
  return { label, all: [...label, ...words(column.group), ...idWords, column.id.toLowerCase()] }
}

/** The columns a query matches, best first: a label that starts with the query,
 *  then one whose words all match, then a match on the group or the id.
 *
 *  Every word of the query must START a word of the column's label, group or id
 *  (or its whole id). Matching anywhere inside the run-together id made "DS"
 *  match `debtYieldStabilized` ("yiel-ds-tabilized"): on the 2 Nov 2026
 *  run-through typing "DS" for DSCR added a Stabilized debt yield column (T-31). */
export function matchColumns(
  options: readonly ColumnDef[],
  selected: readonly ColumnId[],
  query: string,
  group: ColumnGroup | 'All' = 'All',
): ColumnDef[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  const taken = new Set<ColumnId>(selected)
  const ranked: Array<{ column: ColumnDef; rank: number; order: number }> = []
  options.forEach((column, order) => {
    if (taken.has(column.id)) return
    if (group !== 'All' && column.group !== group) return
    const label = column.label.toLowerCase()
    const tokens = searchTokens(column)
    const starts = (pool: string[], word: string) => pool.some((token) => token.startsWith(word))
    if (!words.every((word) => starts(tokens.all, word))) return
    const rank = words.length === 0
      ? 0
      : label.startsWith(words[0]) ? 0 : words.every((word) => starts(tokens.label, word)) ? 1 : 2
    ranked.push({ column, rank, order })
  })
  ranked.sort((a, b) => a.rank - b.rank || a.order - b.order)
  return ranked.map((entry) => entry.column)
}
